/**
 * Per-account pricing configuration state — the working copy a billing
 * admin edits when setting default prices and per-site-group/site
 * overrides for device categories and billable features on one account.
 *
 * Modeled on the consolidated-settings feature pattern
 * (`features/_settings/createSettingsFeature.js`): one document-style
 * store keyed by config kind ('device' | 'feature') via `keyFor`, with
 * memoized per-kind selectors. Each kind holds a load-then-edit working
 * copy — load/save round-trip status plus an `originalConfigs` snapshot
 * for cancel and dirty tracking — so edits survive across grid remounts.
 * `PricingConfigGrid`'s redux mode reads and drives this; its controlled
 * (inline-form) mode keeps its own local copy and never touches it.
 *
 * Contains:
 * - the `pricingConfig` slice (device + feature working copies)
 * - edit/override action creators keyed by configType
 * - selectors (configs, loading, saving, editMode, error, hasChanges)
 */
import { createSelector, createSlice } from '@reduxjs/toolkit';

import {
  loadDevicePricingConfig,
  loadFeaturePricingConfig,
  saveDevicePricingConfig,
  saveFeaturePricingConfig,
} from '@sophocles/features/billing/api';
import {
  PRICING_CONFIG_ROW_KEY,
  PRICING_CONFIG_TYPES,
  pricingConfigKeyFor,
} from '@sophocles/features/billing/pricingConfigKeys';
import { memoizeByKey } from '@sophocles/utils/memoizeByKey';

// The load/save thunks differ per kind only in endpoint + toast copy, so
// they stay in `billing/api`; this slice wires both through one keyed set
// of reducer bodies.
const PRICING_CONFIG_THUNKS = {
  device: { load: loadDevicePricingConfig, save: saveDevicePricingConfig },
  feature: { load: loadFeaturePricingConfig, save: saveFeaturePricingConfig },
};

const makeSection = () => ({
  configs: [],
  originalConfigs: [],
  loading: false,
  saving: false,
  error: null,
  editMode: false,
  accountId: null,
});

const initialState = PRICING_CONFIG_TYPES.reduce(
  (state, configType) => ({ ...state, [pricingConfigKeyFor(configType)]: makeSection() }),
  {},
);

// Server sends null/empty default prices; the grid edits them as strings,
// so normalize to '0' on the way in.
const normalizeConfigs = configs =>
  (configs || []).map(config => ({
    ...config,
    default_price:
      config.default_price === null || config.default_price === undefined || config.default_price === ''
        ? '0'
        : config.default_price,
  }));

const slice = createSlice({
  name: 'pricingConfig',
  initialState,
  reducers: {
    setPricingConfigEditMode(state, action) {
      const { configType, editMode } = action.payload;
      const section = state[pricingConfigKeyFor(configType)];
      section.editMode = editMode;
      if (editMode) {
        section.originalConfigs = JSON.parse(JSON.stringify(section.configs));
      }
    },
    cancelPricingConfigEdit(state, action) {
      const { configType } = action.payload;
      const section = state[pricingConfigKeyFor(configType)];
      section.configs = JSON.parse(JSON.stringify(section.originalConfigs));
      section.editMode = false;
    },
    updatePricingConfig(state, action) {
      const { configType, rowKey, updates } = action.payload;
      const section = state[pricingConfigKeyFor(configType)];
      const index = section.configs.findIndex(c => PRICING_CONFIG_ROW_KEY[configType](c) === rowKey);
      if (index !== -1) {
        section.configs[index] = { ...section.configs[index], ...updates };
      }
    },
    addPricingConfigOverride(state, action) {
      const { configType, rowKey, override } = action.payload;
      const section = state[pricingConfigKeyFor(configType)];
      const config = section.configs.find(c => PRICING_CONFIG_ROW_KEY[configType](c) === rowKey);
      if (config) {
        config.overrides = [...(config.overrides || []), override];
      }
    },
    removePricingConfigOverride(state, action) {
      const { configType, rowKey, overrideIndex } = action.payload;
      const section = state[pricingConfigKeyFor(configType)];
      const config = section.configs.find(c => PRICING_CONFIG_ROW_KEY[configType](c) === rowKey);
      if (config && config.overrides) {
        config.overrides = config.overrides.filter((_, i) => i !== overrideIndex);
      }
    },
    updatePricingConfigOverride(state, action) {
      const { configType, rowKey, overrideIndex, updates } = action.payload;
      const section = state[pricingConfigKeyFor(configType)];
      const config = section.configs.find(c => PRICING_CONFIG_ROW_KEY[configType](c) === rowKey);
      if (config && config.overrides && config.overrides[overrideIndex]) {
        config.overrides[overrideIndex] = { ...config.overrides[overrideIndex], ...updates };
      }
    },
    resetPricingConfig(state, action) {
      const { configType } = action.payload;
      state[pricingConfigKeyFor(configType)] = makeSection();
    },
  },
  extraReducers: builder => {
    PRICING_CONFIG_TYPES.forEach(configType => {
      const key = pricingConfigKeyFor(configType);
      const { load, save } = PRICING_CONFIG_THUNKS[configType];
      builder
        .addCase(load.pending, state => {
          state[key].loading = true;
          state[key].error = null;
        })
        .addCase(load.fulfilled, (state, action) => {
          state[key].loading = false;
          const configs = normalizeConfigs(action.payload.configs);
          state[key].configs = configs;
          state[key].originalConfigs = JSON.parse(JSON.stringify(configs));
          state[key].accountId = action.payload.accountId;
          state[key].editMode = false;
        })
        .addCase(load.rejected, (state, action) => {
          state[key].loading = false;
          state[key].error = action.payload;
        })
        .addCase(save.pending, state => {
          state[key].saving = true;
          state[key].error = null;
        })
        .addCase(save.fulfilled, (state, action) => {
          state[key].saving = false;
          state[key].configs = action.payload.configs || [];
          state[key].originalConfigs = JSON.parse(JSON.stringify(action.payload.configs || []));
          state[key].editMode = false;
        })
        .addCase(save.rejected, (state, action) => {
          state[key].saving = false;
          state[key].error = action.payload;
        });
    });
  },
});

/***
 * Pricing Config — factory selectors keyed by config type ('device' | 'feature').
 *
 * Each factory is memoized per configType (via memoizeByKey) so
 * `useSelector(selectX('device'))` gets the same selector instance across
 * renders — otherwise every render would build a fresh `createSelector` and
 * lose its memoization.
 */

export const selectPricingConfigState = configType => state => state.pricingConfig[pricingConfigKeyFor(configType)];
export const selectPricingConfigs = memoizeByKey(configType =>
  createSelector(selectPricingConfigState(configType), state => state.configs || []),
);
export const selectPricingConfigLoading = memoizeByKey(configType =>
  createSelector(selectPricingConfigState(configType), state => state.loading || false),
);
export const selectPricingConfigSaving = memoizeByKey(configType =>
  createSelector(selectPricingConfigState(configType), state => state.saving || false),
);
export const selectPricingConfigEditMode = memoizeByKey(configType =>
  createSelector(selectPricingConfigState(configType), state => state.editMode || false),
);
export const selectPricingConfigError = memoizeByKey(configType =>
  createSelector(selectPricingConfigState(configType), state => state.error || null),
);
export const selectPricingConfigHasChanges = memoizeByKey(configType =>
  createSelector(
    selectPricingConfigState(configType),
    state => JSON.stringify(state.configs) !== JSON.stringify(state.originalConfigs),
  ),
);

export const {
  setPricingConfigEditMode,
  cancelPricingConfigEdit,
  updatePricingConfig,
  addPricingConfigOverride,
  removePricingConfigOverride,
  updatePricingConfigOverride,
  resetPricingConfig,
} = slice.actions;

export default slice.reducer;
